import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Field, Loading, Modal, Select, TextInput, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * The register of third parties personal data reaches through SARTHI.
 * Region and contract status stay UNKNOWN until established from the
 * contract - never guessed.
 */

interface Processor {
  id: string;
  provider: string;
  purpose: string;
  dataCategories: string[];
  dataTransferred: string;
  environment: string;
  region: string;
  contractStatus: 'UNKNOWN' | 'NOT_STARTED' | 'IN_REVIEW' | 'SIGNED' | 'NOT_REQUIRED';
  active: boolean;
  notes: string | null;
}

const CONTRACT_LABELS: Record<Processor['contractStatus'], string> = {
  UNKNOWN: 'Unknown',
  NOT_STARTED: 'Not started',
  IN_REVIEW: 'In review',
  SIGNED: 'Signed',
  NOT_REQUIRED: 'Not required',
};

export function PlatformProcessorsTab() {
  const { session } = useAuth();
  const isSuperadmin = session?.platformRole === 'SUPERADMIN';
  const [editing, setEditing] = useState<Processor | 'new' | null>(null);
  const list = useQuery({
    queryKey: ['platform-processors'],
    queryFn: () => api.get<Processor[]>('/api/platform/privacy/processors'),
  });
  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const rows = list.data ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <h3>Processor register</h3>
        {isSuperadmin ? (
          <button className="btn sm primary" onClick={() => setEditing('new')}>
            Add a processor
          </button>
        ) : null}
      </div>
      <div className="card-body small muted">
        Everyone outside SARTHI that personal data reaches. Hotels see the active entries. Record a region and a
        contract status only once they are established from the contract or the provider.
      </div>
      {rows.length === 0 ? (
        <Empty message="No processors recorded" />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Provider</th>
                <th>What for</th>
                <th>Region</th>
                <th>Contract</th>
                <th>Environment</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={row.active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <b>{row.provider}</b>
                    <div className="xs muted">{row.dataCategories.join(', ')}</div>
                  </td>
                  <td className="small">
                    {row.purpose}
                    <div className="xs muted">{row.dataTransferred}</div>
                  </td>
                  <td>{row.region === 'UNKNOWN' ? <span className="chip warn">Unknown</span> : row.region}</td>
                  <td>
                    {row.contractStatus === 'UNKNOWN' ? (
                      <span className="chip warn">Unknown</span>
                    ) : (
                      CONTRACT_LABELS[row.contractStatus]
                    )}
                  </td>
                  <td>{row.environment}</td>
                  <td>
                    {isSuperadmin ? (
                      <button className="btn sm" onClick={() => setEditing(row)}>
                        Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing ? <ProcessorEditor processor={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function ProcessorEditor({ processor, onClose }: { processor: Processor | null; onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    provider: processor?.provider ?? '',
    purpose: processor?.purpose ?? '',
    dataCategories: (processor?.dataCategories ?? []).join(', '),
    dataTransferred: processor?.dataTransferred ?? '',
    environment: processor?.environment ?? 'ALL',
    region: processor?.region ?? 'UNKNOWN',
    contractStatus: processor?.contractStatus ?? 'UNKNOWN',
    active: processor?.active ?? true,
    notes: processor?.notes ?? '',
  });
  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        dataCategories: form.dataCategories
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean),
      };
      return processor
        ? api.put(`/api/platform/privacy/processors/${processor.id}`, payload)
        : api.post('/api/platform/privacy/processors', payload);
    },
    onSuccess: () => {
      push('Saved', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-processors'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title={processor ? processor.provider : 'Add a processor'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={save.isPending || !form.provider.trim()} onClick={() => save.mutate()}>
            Save
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <TextInput label="Provider" value={form.provider} onChange={(e) => set({ provider: e.target.value })} />
        <Field label="What SARTHI uses it for">
          <textarea className="input" rows={2} value={form.purpose} onChange={(e) => set({ purpose: e.target.value })} />
        </Field>
        <TextInput
          label="Kinds of data"
          hint="Comma separated"
          value={form.dataCategories}
          onChange={(e) => set({ dataCategories: e.target.value })}
        />
        <Field label="What is sent to it">
          <textarea className="input" rows={2} value={form.dataTransferred} onChange={(e) => set({ dataTransferred: e.target.value })} />
        </Field>
        <div className="form-grid">
          <TextInput label="Environment" value={form.environment} onChange={(e) => set({ environment: e.target.value })} />
          <TextInput
            label="Region"
            hint="UNKNOWN until established"
            value={form.region}
            onChange={(e) => set({ region: e.target.value })}
          />
          <Select
            label="Contract"
            value={form.contractStatus}
            onChange={(e) => set({ contractStatus: e.target.value as Processor['contractStatus'] })}
          >
            {Object.entries(CONTRACT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
          In use
        </label>
        <Field label="Notes (SARTHI only)">
          <textarea className="input" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

interface HotelProcessor {
  provider: string;
  purpose: string;
  dataCategories: string[];
  dataTransferred: string;
  region: string;
}

/** The hotel's view: who SARTHI uses, for what, and what reaches them. */
export function HotelProcessorsTab() {
  const list = useQuery({
    queryKey: ['privacy-processors'],
    queryFn: () => api.get<HotelProcessor[]>('/api/privacy/processors'),
  });
  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Who SARTHI shares data with</h3>
      </div>
      <div className="card-body small muted">
        The services SARTHI uses to run your account, and what reaches each. Where a region shows as unknown it has
        not yet been confirmed with the provider.
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-stack">
          <thead>
            <tr>
              <th>Provider</th>
              <th>What for</th>
              <th>What reaches it</th>
              <th>Region</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((row) => (
              <tr key={row.provider}>
                <td className="b">{row.provider}</td>
                <td className="small">{row.purpose}</td>
                <td className="small">{row.dataTransferred}</td>
                <td>{row.region === 'UNKNOWN' ? 'Not yet confirmed' : row.region}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
